import { LitElement, css, html } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { calculateFitSize } from '../utils/fittext-calculator.js'

export default class FitText extends LitElement {
  static properties = {
    text: { attribute: "text", type: String },
    fontFamily: { attribute: "font-family", type: String },
    styleName: { attribute: "style-name", type: String },
    minSize: { attribute: "min-size", type: Number },
    maxSize: { attribute: "max-size", type: Number },
    defaultSize: { attribute: "default-size", type: Number },
    _maxSize: { attribute: false, type: Number },
    lineHeight: { attribute: "line-height", type: Number },
    textAlign: { attribute: "text-align", type: String },
    enableFittext: { attribute: "enable-fittext", type: String },
    autoMobileFontSize: { attribute: "auto-mobile-font-size", type: Boolean },
    _calculatedSize: { attribute: false }
  }

  // Mobile font size factor (60% of desktop size)
  static MOBILE_FONT_SIZE_FACTOR = 0.6
  static MOBILE_BREAKPOINT = 768 // px

  static styles = css`
    :host {
      position: relative;
    }

    .container {
      max-width: 100%;
      width: 100%;
      overflow: hidden;
    }

    .font-info {
      margin-top: 1rem;
      margin-bottom: 0.875rem;
      pointer-events: none;
    }

    .text {
      margin: 0;
      padding: 0;
    }

    .text[contenteditable] {
      cursor: text;
      outline: none;
    }

    .text[contenteditable]:focus {
      outline: 2px solid #007acc;
      outline-offset: 2px;
    }
  `

  constructor() {
    super()
    this.text = "Sample text"
    this.fontFamily = "sans-serif"
    this.styleName = ""
    this.enableFittext = "true"
    this.defaultSize = 24
    this._calculatedSize = 24 // Default fallback instead of undefined maxSize
    this._resizeTimeout = null
    this._boundResizeHandler = this._handleResize.bind(this)
  }

  async connectedCallback() {
    super.connectedCallback()
    this.minSize = this.minSize || 12
    this._maxSize = this.maxSize || 300
    this.defaultSize = this.defaultSize || 24
    this.lineHeight = this.lineHeight || 1.2
    this.textAlign = this.textAlign || 'left'
    window.addEventListener('resize', this._boundResizeHandler)
    await this.updateComplete
    await this._calculateSize()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this._boundResizeHandler)
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout)
    }
  }

  _isMobileViewport() {
    return window.innerWidth < FitText.MOBILE_BREAKPOINT
  }

  async _calculateSize() {
    const isFittextEnabled = this.enableFittext === 'true' || this.enableFittext === true

    if (!isFittextEnabled) {
      // Use fixed size when fittext is disabled
      // Apply mobile font-size factor if enabled and on mobile viewport
      if (this.autoMobileFontSize && this._isMobileViewport()) {
        this._calculatedSize = Math.max(
          Math.floor(this.defaultSize * FitText.MOBILE_FONT_SIZE_FACTOR),
          this.minSize
        )
        console.log('[FITTEXT] Mobile default-size applied:', this._calculatedSize, 'from defaultSize:', this.defaultSize)
      } else {
        this._calculatedSize = this.defaultSize
      }
      this.requestUpdate()
      return
    }

    // Get container for measurement
    const container = this.shadowRoot?.querySelector('.container')
    if (!container) return

    // Validate font family exists
    if (!this.fontFamily || this.fontFamily.trim() === '') {
      console.warn('[FITTEXT] No font family specified, using fallback')
      this._calculatedSize = this.defaultSize
      this.requestUpdate()
      return
    }

    // Use shared utility for calculation
    try {
      this._calculatedSize = await calculateFitSize({
        text: this.text,
        fontFamily: this.fontFamily,
        styleName: this.styleName,
        containerWidth: container.offsetWidth,
        minSize: this.minSize,
        maxSize: this._maxSize,
        lineHeight: this.lineHeight,
        variationSettings: 'normal',
        safetyMargin: 0.97,
        throwOnFontLoadError: false
      })
    } catch (error) {
      console.warn('[FITTEXT] Calculation failed, using fallback size:', error)
      this._calculatedSize = this.defaultSize
    }

    this.requestUpdate()
  }

  _handleResize() {
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout)
    }
    this._resizeTimeout = setTimeout(async () => {
      await this._calculateSize()
    }, 100)
  }

  _onTextInput(e) {
    this.text = e.target.textContent
    requestAnimationFrame(async () => {
      await this._calculateSize()
    })
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (changedProperties.has('text') ||
      changedProperties.has('fontFamily') ||
      changedProperties.has('minSize') ||
      changedProperties.has('maxSize') ||
      changedProperties.has('defaultSize') ||
      changedProperties.has('enableFittext') ||
      changedProperties.has('autoMobileFontSize') ||
      changedProperties.has('lineHeight')) {
      requestAnimationFrame(async () => {
        await this._calculateSize()
      })
    }
  }

  async firstUpdated() {
    // Wait for fonts to be fully loaded before calculating size
    await document.fonts.ready
    requestAnimationFrame(async () => {
      await this._calculateSize()
    })
  }

  render() {
    const fontDisplay = this.styleName ? `${this.fontFamily} ${this.styleName}` : this.fontFamily;
    const combinedFont = this.styleName ? `"${this.fontFamily} ${this.styleName}"` : `"${this.fontFamily}"`;
    const isFittextEnabled = this.enableFittext === 'true' || this.enableFittext === true;

    const textStyles = isFittextEnabled
      ? `display: inline-block; font-family: ${combinedFont}; font-size: ${this._calculatedSize}px; line-height: ${this.lineHeight};`
      : `display: block; word-wrap: break-word; overflow-wrap: break-word; font-family: ${combinedFont}; font-size: ${this._calculatedSize}px; line-height: ${this.lineHeight};`;

    return html`
      <div class="font-info">
        ${fontDisplay} ${this._calculatedSize}px
      </div>
      <div class="container" style="text-align: ${this.textAlign}">
        <p class="text"
           @input=${this._onTextInput}
           style="${textStyles}">
          ${unsafeHTML(this.text)}
        </p>
      </div>
    `
  }
}

// Define the custom element
customElements.define('fit-text', FitText)
